// Rendert PDF-Seiten clientseitig (pdf.js) zu kleinen Vorschau-Bildern (PNG-DataURLs)
// für den Seiten-Editor. Kein Server-Bildrendering nötig.
import * as pdfjsLib from 'pdfjs-dist';
// Vite: Worker als URL einbinden.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
// Decoder für JPEG2000/JBIG2-Scans (siehe vite.config.ts, Plugin pdfjs-wasm).
const PDFJS_WASM_URL = `${import.meta.env.BASE_URL}pdfjs-wasm/`;

export interface PageThumb {
  /** 1-basierte Seitennummer. */
  page: number;
  /** PNG-DataURL der gerenderten Seite. */
  dataUrl: string;
  width: number;
  height: number;
}

/**
 * Rendert alle Seiten eines PDFs zu Thumbnails. `data` ist der PDF-Inhalt
 * (ArrayBuffer/Uint8Array). `maxWidth` begrenzt die Thumbnail-Breite.
 * pdf.js detacht den übergebenen Buffer — daher intern kopieren.
 */
export async function renderPdfThumbnails(data: ArrayBuffer | Uint8Array, maxWidth = 150): Promise<PageThumb[]> {
  const bytes = data instanceof Uint8Array ? data.slice() : new Uint8Array(data.slice(0));
  const loadingTask = pdfjsLib.getDocument({ data: bytes, wasmUrl: PDFJS_WASM_URL });
  const pdf = await loadingTask.promise;
  const thumbs: PageThumb[] = [];
  try {
    for (let i = 1; i <= pdf.numPages; i++) {
      const canvas = document.createElement('canvas');
      try {
        const page = await pdf.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = maxWidth / base.width;
        const viewport = page.getViewport({ scale });
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        // pdf.js v6: Canvas-Element übergeben (kanonisch).
        await page.render({ canvas, viewport } as any).promise;
        thumbs.push({ page: i, dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height });
        page.cleanup();
      } catch (err) {
        // EINE problematische Seite (z.B. exotisches Bildformat) darf die Vorschau nicht killen
        // → grauer Platzhalter, Seite bleibt wähl-/verschiebbar.
        // eslint-disable-next-line no-console
        console.warn(`Thumbnail Seite ${i} fehlgeschlagen:`, err);
        const w = Math.ceil(maxWidth), h = Math.ceil(maxWidth * 1.414);
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (ctx) { ctx.fillStyle = '#f1f5f9'; ctx.fillRect(0, 0, w, h); ctx.fillStyle = '#94a3b8'; ctx.font = '11px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(`Seite ${i}`, w / 2, h / 2); }
        thumbs.push({ page: i, dataUrl: canvas.toDataURL('image/png'), width: w, height: h });
      }
    }
  } finally {
    // pdf.js v6: aufgeräumt wird über den LoadingTask (PDFDocumentProxy hat kein destroy()).
    try { await loadingTask.destroy(); } catch { /* egal */ }
  }
  return thumbs;
}
